import { ReactNode, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import './Layout.css';

interface LayoutProps {
  children: ReactNode;
  user?: any;
  signOut?: () => void;
}

export default function Layout({ children, user, signOut }: LayoutProps) {
  const location = useLocation();
  const isOnline = useNetworkStatus();

  // Run diagnostics and restart DataStore when user is authenticated
  useEffect(() => {
    if (user) {
      // Wait a bit for DataStore to initialize, then restart it with auth
      setTimeout(async () => {
        try {
          // Restart DataStore to enable sync with authentication
          // DISABLED: restartDataStoreAfterAuth causes data loss
          // - It restarts DataStore, which triggers cloud sync BEFORE outbox is processed
          // - Schema is now 'allow: private', so owner field fixes are not needed
          // - DataStore should NOT be restarted - it persists across sessions
          // console.log('⚠️ restartDataStoreAfterAuth is disabled to prevent data loss');
          // DataStore automatically handles sync - no restart needed
          
          // Run deep diagnostic
          const { deepSyncDiagnostic } = await import('../utils/deepSyncDiagnostic');
          await deepSyncDiagnostic();
        } catch (error) {
          console.error('Error in post-auth setup:', error);
        }
      }, 2000);
    }
  }, [user]);

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: '📊' },
    { path: '/ptp', label: 'PTP', icon: '📋' },
    { path: '/ptp/analytics', label: 'PTP Analytics', icon: '📊' },
    { path: '/data', label: 'Data', icon: '📁' },
    { path: '/reports', label: 'Reports', icon: '📄' },
    { path: '/analytics', label: 'Analytics', icon: '📈' },
    { path: '/users', label: 'Users', icon: '👥' },
    { path: '/sync', label: 'Sync', icon: '🔄' },
    { path: '/settings', label: 'Settings', icon: '⚙️' },
  ];

  return (
    <div className="layout">
      <header className="header">
        <div className="header-content">
          <h1 className="logo">OfflineSync</h1>
          <div className="header-status">
            <span className={`status-indicator ${isOnline ? 'online' : 'offline'}`}>
              {isOnline ? '🟢 Online' : '🔴 Offline'}
            </span>
            {user && (
              <div style={{ marginLeft: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '14px' }}>👤 {user.username}</span>
                {signOut && (
                  <button 
                    onClick={signOut}
                    style={{
                      padding: '5px 15px',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    Sign Out
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="layout-body">
        <nav className="sidebar">
          <ul className="nav-list">
            {navItems.map(item => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`nav-link ${location.pathname === item.path || location.pathname.startsWith(item.path + '/') ? 'active' : ''}`}
                >
                  <span className="nav-icon">{item.icon}</span>
                  <span className="nav-label">{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <main className="main-content">
          {children}
        </main>
      </div>
    </div>
  );
}

